import axios from 'axios';
import { apiService } from './api.service';
import {
  extractData,
  handleResponse,
  convertToPageable,
  handleApiException,
} from '../helpers/apiResponseHelper';
import {
  ICustomerCardType,
  ICustomerCardRequest,
  CustomerCardActivateResult,
} from '../models/customer-card';

export enum CardCategory {
  ALL_FUELS = 'ALL_FUELS',
  ALL_FUELS_AND_VEHICLE_PRODUCTS = 'ALL_FUELS_AND_VEHICLE_PRODUCTS',
  ALL_FUELS_AND_ALL_PRODUCTS = 'ALL_FUELS_AND_ALL_PRODUCTS',
  DIESEL_FUEL = 'DIESEL_FUEL',
}

export interface INewCardRequestData {
  cardName: string;
  cardCategory?: CardCategory;
  cardType: ICustomerCardType;
  monthlyLimit: string;
  customerId?: number;
  customerContactId?: number;
  pickupLocation: string;
}

export interface IdCreditCardRequest {
  email: string;
  phone: string;
  pinCode: string;
  address: string;
  city: string;
  county: string;
  zip: string;
  includeRfidCard: boolean;
}

export interface UpdateCardLimitRequest {
  remoteCardId: string;
  newLimit: string;
  isBlocked: boolean;
}

export interface ChangeIdCreditPinRequest {
  remoteCardId: string;
  newPin: string;
}

async function getCustomerCards(
  customerId: number,
  cardType: ICustomerCardType | null,
  pageRequest: ICustomerCardRequest
) {
  const request = { ...pageRequest };
  if (cardType) {
    request.cardType = cardType;
  }
  return axios
    .get(apiService.getApiUrl() + '/cards/' + customerId, {
      params: request,
      withCredentials: true,
    })
    .then(handleResponse)
    .then(convertToPageable)
    .catch(handleApiException);
}

async function postIdCreditCardRequest(request: IdCreditCardRequest) {
  return axios
    .post(
      apiService.getApiUrl() + '/cards/request-id-credit',
      {
        ...request,
      }
    )
    .then(handleResponse)
    .then(extractData)
    .catch(handleApiException);
}

async function postIdCreditEmailConfirm(base64requestId: string) {

  return axios
    .post(
      apiService.getApiUrl() + '/cards/confirm-id-credit/' + base64requestId,
      {}
    )
    .then(handleResponse)
    .then(extractData)
    .catch(handleApiException);
}

async function postCardLimit(customerId: number, request: UpdateCardLimitRequest, contactPersonId: number) {
  return axios
    .post(
      apiService.getApiUrl() + '/cards/' + customerId + '/limit',
      request,
      { 
        params: {
          contactPersonId
        } 
    }
    )
    .then(handleResponse)
    .then(extractData)
    .catch(handleApiException);
}

async function changeIdCreditPin(customerId: number, request: ChangeIdCreditPinRequest) {
  return axios
    .put(
      apiService.getApiUrl() + '/cards/' + customerId + '/pin',
      request
    )
    .then(handleResponse)
    .catch(handleApiException);
}

async function getCardPin(customerId: number, signingSessionId: string, remoteCardId: string, contactPersonId: number) : Promise<string> {
  return axios
    .post(
      apiService.getApiUrl() + '/cards/' + customerId + '/pin',
      {
        signingSessionId, 
        remoteCardId
      }, {
        params: {
          contactPersonId
        }
      }
    )
    .then(handleResponse)
    .then(extractData)
    .catch(handleApiException);
}

async function activateCards(
  customerId: number, 
  signingSessionId: string, 
  remoteCardIds: string[],
  contactPersonId: number
) : Promise<CustomerCardActivateResult> {
  return axios
    .post(
      apiService.getApiUrl() + '/cards/' + customerId + '/activate',
      {
        signingSessionId, 
        remoteCardIds
      }, {
        params: {
          contactPersonId
        }
      }
    )
    .then(handleResponse)
    .then(extractData)
    .catch(handleApiException);
}

function formatCardNumber(remoteCardId: string) {
  if (!remoteCardId) {
    return '';
  }
  const numberSegments = segmentCardNumber(remoteCardId);

  return numberSegments.join(' ');
}

function segmentCardNumber(remoteCardId: string) {
    if (remoteCardId.startsWith("IDC"))
        return [remoteCardId];

    const segments = [];
    for (let i = 0; i < remoteCardId.length; i) {
        segments.push(remoteCardId.substr(i, 4));
        i = i + 4;
    }
    return segments;
}

const CustomerCardService = {
  activateCards,
  getCardPin,
  getCustomerCards,
  postCardLimit,
  changeIdCreditPin,
  formatCardNumber,
  postIdCreditCardRequest,
  postIdCreditEmailConfirm
}

export default CustomerCardService;
